// The Models library screen (SCR-153, SCR-574..576, SCR-588..590, ADR-101, ADR-103, ADR-115):
// which models each agent gets, shown in both shells, in four sub-tabs (REQ-239). Each is a page of
// its own with its own Save and unsaved guard:
//   Inventory  the models an installation can call, grouped by provider. On the proxy it is the
//              proxy's own installation (`inventoryInstallationId`), with no picker and no name; on
//              the billing admin it is the installation in the scope selector, or each one by name
//              at the org default.
//   Families   the org's global allow-list, one row per provider; Edit opens the provider modal.
//   Fallbacks  the fallback chains alone.
//   Agents     one card per agent, whose default is picked from what the list allows.
// The scope is the org default or one installation; the global list and the fallbacks are the
// org's, so an installation shows them read-only. The scope selector is the AllCode admin's
// (installation scope is a staff capability, REQ-214); the proxy's Admin area mounts the org scope
// for its writes. The surface decides what the principal may edit (`fieldMode`). The screen never
// reads the URL: the shell hands it `subtab` and `subtabHref` (primitives/subtabs.tsx).
import { useEffect, useState } from "preact/hooks";
import type { ApiClient } from "../../api/client";
import { useApi } from "../../api/context";
import { ApiError } from "../../api/errors";
import type { OrgContext, Surface } from "../../api/org-context";
import { useHost } from "../../host/host";
import { useReadOnly } from "../../host/readonly";
import { StaleNote } from "../../primitives/stale-note";
import { fieldErrorsFrom, hasErrors } from "../../primitives/forms";
import type { HelpId } from "../../primitives/help";
import { Card, Chip } from "../../primitives/page";
import { Empty, ErrorBanner, Loading } from "../../primitives/states";
import { firstVisible, SubTabs, type SubTabDef } from "../../primitives/subtabs";
import { getPath, isInherited, merge, setPath, type Json, type JsonObject } from "../../settings/inherit";
import { ScopeSelector, useDraftGuard, type ScopeOption } from "../../settings/scope-selector";
import { validate } from "../../settings/validate";
import { agentCards, agentErrorsFrom, effectiveLists, isPlacedRefusal, narrowChoices, type EffectiveModels } from "./agents-model";
import { AgentsSection } from "./agents-section";
import { appliedSummary, groupInventory, HARNESS_NOTE, HARNESSES, inventoryIsEmpty, pinsFromDocument, writable, writeHeaders, type Harness, type ModelInventory, type SettingsLayers } from "./model";
import { ProviderModal } from "./provider-modal";
import { applyProviderEdit, catalogOf, entryFor, globalOf, providerRows, type CatalogModel, type ProviderEditResult } from "./provider-model";
import { ProvidersSection } from "./providers-section";
import { FALLBACKS_PATH, fallbackErrorsFrom } from "./fallbacks-model";
import { FallbacksSection } from "./fallbacks-section";

/** The Models sub-tabs (NAV-039, REQ-239), in display order. Whoever opens Models sees all four. */
export const MODELS_SUBTABS: readonly SubTabDef[] = [
  { id: "inventory", label: "Inventory", visible: () => true },
  { id: "families", label: "Families", visible: () => true },
  { id: "fallbacks", label: "Fallbacks", visible: () => true },
  { id: "agents", label: "Agents", visible: () => true },
];

const NOTE_HELP: HelpId = "settings.models.notSecurity";
const INVENTORY_HELP: HelpId = "settings.models.inventory";

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

/** The scope a shell mounts the screen on: the org default, or one installation's override. */
export type ModelsScope = "org" | { installationId: string };

/** The scope as the API paths and the selector see it: "" is the org default. */
export function scopeId(scope: ModelsScope): string {
  return scope === "org" ? "" : scope.installationId;
}

/** What the tab reads and writes for one scope. */
export interface Loaded {
  /** The document underneath the edited one: absent at the org default. */
  inherited: JsonObject | undefined;
  /** The edited document: the org default, or an installation's override. */
  document: JsonObject;
  version: number;
  pins: ReturnType<typeof pinsFromDocument>;
  layers: SettingsLayers | null;
  /** The API's per-agent lists for the saved document (API-573); absent from an API that predates it. */
  effectiveModels: EffectiveModels | undefined;
}

export async function loadScope(client: ApiClient, orgId: string, scope: string): Promise<Loaded> {
  if (scope === "") {
    const org = await client.get("/v1/orgs/{orgId}/installation-settings", { path: { orgId } });
    return { inherited: undefined, document: org.document as unknown as JsonObject, version: org.version, pins: {}, layers: null, effectiveModels: org.effectiveModels };
  }
  const layers = await client.get("/v1/orgs/{orgId}/installations/{installationId}/settings", { path: { orgId, installationId: scope } });
  return {
    inherited: layers.default.document as unknown as JsonObject,
    document: (layers.override?.override ?? {}) as unknown as JsonObject,
    version: layers.override?.version ?? 0,
    pins: pinsFromDocument(layers.pins as unknown as Json),
    layers,
    effectiveModels: layers.effectiveModels,
  };
}

/** Writes the scope with the version read (`If-Match`). */
export async function saveScope(client: ApiClient, orgId: string, scope: string, loaded: Loaded, draft: JsonObject): Promise<void> {
  const headers = writeHeaders(loaded.version);
  // The form edits the document as untyped JSON; the generated body types describe the
  // schema's shape (and the partial as an open object), so the body alone is cast here.
  if (scope === "") {
    // The API serves the default whole — the server's own `org` and `sign_in.served_by` included —
    // and refuses a write that carries them (server_generated / field_staff_only); they are the
    // server's to fill, so they are left out of what is sent, as the API itself strips them.
    const document = writable(merge(loaded.document, draft) as JsonObject);
    type DefaultBody = Parameters<typeof client.put<"/v1/orgs/{orgId}/installation-settings">>[1]["body"];
    await client.put("/v1/orgs/{orgId}/installation-settings", { path: { orgId }, body: { document } as unknown as DefaultBody, headers });
    return;
  }
  type OverrideBody = Parameters<typeof client.put<"/v1/orgs/{orgId}/installations/{installationId}/settings">>[1]["body"];
  await client.put("/v1/orgs/{orgId}/installations/{installationId}/settings", {
    path: { orgId, installationId: scope },
    body: { override: draft } as unknown as OverrideBody,
    headers,
  });
}

/** How many inventories the org default reads at once: the BFF limits a session to 20 requests a second. */
const INVENTORY_BATCH = 4;

/** One installation's inventory read: what it reported, or what the read threw. */
export type InventoryRead = { installationId: string; inventory: ModelInventory } | { installationId: string; error: unknown };

/** Each installation's inventory, read a few at a time, in the order given; a failed read is kept beside the others. */
export async function readInventories(client: ApiClient, orgId: string, installationIds: readonly string[]): Promise<InventoryRead[]> {
  const out: InventoryRead[] = [];
  for (let i = 0; i < installationIds.length; i += INVENTORY_BATCH) {
    const ids = installationIds.slice(i, i + INVENTORY_BATCH);
    const results = await Promise.allSettled(ids.map((installationId) => client.get("/v1/orgs/{orgId}/installations/{installationId}/models", { path: { orgId, installationId } })));
    results.forEach((result, n) => {
      const installationId = ids[n] as string;
      out.push(result.status === "fulfilled" ? { installationId, inventory: result.value } : { installationId, error: result.reason as unknown });
    });
  }
  return out;
}

/** The inventories the reads produced, and what the first failed read threw when every one failed (else null). */
function reportedOf(reads: readonly InventoryRead[]): { inventories: ModelInventory[]; failure: unknown } {
  const inventories = reads.flatMap((read) => ("inventory" in read ? [read.inventory] : []));
  const failed = reads.find((read) => "error" in read);
  return { inventories, failure: inventories.length === 0 && failed && "error" in failed ? failed.error : null };
}

/**
 * The inventories the org default's catalog is the union of: every active installation's, read a
 * few at a time. One that fails is left out (the others still name the org's models); when every
 * one fails the first failure is the answer.
 */
export async function loadOrgInventories(client: ApiClient, orgId: string, installationIds: readonly string[]): Promise<ModelInventory[]> {
  const { inventories, failure } = reportedOf(await readInventories(client, orgId, installationIds));
  if (failure !== null) throw failure;
  return inventories;
}

export interface ModelsTabProps {
  context: OrgContext;
  /** The scope shown first, which every write goes to; the AllCode admin's selector may move to another. */
  scope: ModelsScope;
  /** The route's sub-tab segment (`MODELS_SUBTABS`); undefined at the bare section URL, which shows Inventory. */
  subtab: string | undefined;
  /** The shell's URL of one of the Models sub-tabs. */
  subtabHref(id: string): string;
  /**
   * The proxy's own installation, the one Inventory shows, with no picker and no name (SCR-588);
   * null while the proxy has not been told it. Absent on the billing admin, where Inventory follows
   * the scope.
   */
  inventoryInstallationId?: string | null | undefined;
}

export function ModelsTab({ context, scope: initialScope, subtab, subtabHref, inventoryInstallationId }: ModelsTabProps) {
  const { orgId, surface } = context;
  const client = useApi();
  const host = useHost();
  const { readOnly } = useReadOnly();
  const [installations, setInstallations] = useState<ScopeOption[] | null>(null);
  const [scope, setScope] = useState(scopeId(initialScope));
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [reads, setReads] = useState<InventoryRead[] | null>(null);
  const [readError, setReadError] = useState<ApiError | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  // The shown sub-tab's draft differs from what was loaded: the scope selector asks before it moves.
  const [dirty, setDirty] = useState(false);
  const current = subtab ?? (firstVisible(MODELS_SUBTABS, null) as string);

  // "Saved." lives here, not in the sub-tab: a save reloads the scope, which remounts the sub-tab
  // (keyed on the version) and would take its own status line with it (TASK-195).
  const [saved, setSaved] = useState(false);
  useEffect(() => setSaved(false), [current]);
  const load = () => {
    setError(null);
    setLoaded(null);
    setReads(null);
    setReadError(null);
    const listed = client.get("/v1/orgs/{orgId}/installations", { path: { orgId }, query: { limit: 500 } });
    listed.then((page) => setInstallations(page.items.map((item) => ({ id: item.id, label: item.name })))).catch((err: unknown) => setError(unexpected(err)));
    loadScope(client, orgId, scope).then(setLoaded).catch((err: unknown) => setError(unexpected(err)));
    // At the org default the catalog is every active installation's; the proxy's own is read even when the list does not name it.
    const ids =
      scope === ""
        ? listed.then((page) => {
            const active = page.items.filter((item) => item.status === "active").map((item) => item.id);
            return inventoryInstallationId && !active.includes(inventoryInstallationId) ? [...active, inventoryInstallationId] : active;
          })
        : Promise.resolve([scope]);
    ids
      .then((list) => readInventories(client, orgId, list))
      .then(setReads)
      .catch((err: unknown) => setReadError(unexpected(err)));
  };
  useEffect(load, [client, orgId, scope]);

  const moveTo = (next: string) => {
    setSaved(false);
    setScope(next);
  };

  const panel = () => {
    if (error) return <ErrorBanner error={error} onRetry={load} />;
    if (!installations || !loaded) return <Loading label="Loading model settings…" />;
    if (!MODELS_SUBTABS.some((tab) => tab.id === current)) return <Empty message="Models has no page by that name. Choose one of the tabs above." />;
    if (current === "inventory") {
      // The proxy shows its own installation; the billing admin the one in the selector, or each one at the org default.
      const only = inventoryInstallationId !== undefined ? inventoryInstallationId : scope === "" ? undefined : scope;
      return <ModelsInventoryTab only={only} reads={reads} installations={installations} readError={readError} />;
    }
    const { inventories, failure } = reportedOf(reads ?? []);
    if (readError) return <ErrorBanner error={readError} />;
    if (failure !== null) return <ErrorBanner error={unexpected(failure)} />;
    if (!reads) return <Loading label="Loading the model inventory…" />;
    const props: SettingsTabProps = {
      surface,
      scope,
      scopeName: scope === "" ? null : (installations.find((i) => i.id === scope)?.label ?? "this installation"),
      loaded,
      catalog: catalogOf(inventories),
      write: (document) => saveScope(client, orgId, scope, loaded, document),
      onOpenOrg: host.kind === "allcode-admin" && scope !== "" ? () => moveTo("") : undefined,
      onDirty: setDirty,
      onSaved: () => {
        setSaved(true);
        load();
      },
    };
    const key = `${scope}:${loaded.version}`;
    if (current === "families") return <FamiliesTab key={key} {...props} />;
    if (current === "fallbacks") return <FallbacksTab key={key} {...props} />;
    return <AgentsTab key={key} {...props} />;
  };

  return (
    <>
      <SubTabs tabs={MODELS_SUBTABS} scope={null} current={current} href={subtabHref} label="Models" />
      {readOnly && <StaleNote id="models-stale-note" />}
      {host.kind === "allcode-admin" && installations && <ScopeSelector installations={installations} value={scope} onChange={moveTo} dirty={dirty} />}
      {saved && (
        <p class="field-hint" role="status">
          Saved.
        </p>
      )}
      <div id="models-panel">{panel()}</div>
    </>
  );
}

// ------------------------------------------------------------------------------------- Inventory

const NOT_REPORTED = "has not reported its models yet. They appear once its proxy has started and discovered what AWS lets it call.";

interface InventoryTabProps {
  /** The one installation shown, without its name; null when the proxy does not know its own yet; undefined: each installation, by name. */
  only: string | null | undefined;
  /** Each installation's read, in the order asked; null while they load. */
  reads: readonly InventoryRead[] | null;
  installations: readonly ScopeOption[];
  /** The reads could not start (the list failed). */
  readError: ApiError | null;
}

function ModelsInventoryTab({ only, reads, installations, readError }: InventoryTabProps) {
  const body = () => {
    if (only === null) return <Empty message={`This proxy ${NOT_REPORTED}`} />;
    if (readError) return <ErrorBanner error={readError} />;
    if (!reads) return <Loading label="Loading the model inventory…" />;
    if (only !== undefined) {
      const read = reads.find((r) => r.installationId === only);
      return read ? <InventoryOf read={read} subject="This installation" /> : <Empty message={`This proxy ${NOT_REPORTED}`} />;
    }
    if (reads.length === 0) return <Empty message="This organisation has no installation yet. Its models appear once a proxy is installed." />;
    return reads.map((read) => {
      const name = installations.find((i) => i.id === read.installationId)?.label ?? "An installation";
      return (
        <details key={read.installationId} class="inventory-group" open>
          <summary>{name}</summary>
          <InventoryOf read={read} subject={name} />
        </details>
      );
    });
  };
  return (
    <Card title="Model inventory" help={INVENTORY_HELP}>
      {body()}
    </Card>
  );
}

/** One installation's inventory: its table, that it has not reported yet, or why it could not be read. */
function InventoryOf({ read, subject }: { read: InventoryRead; subject: string }) {
  if ("error" in read) return <ErrorBanner error={unexpected(read.error)} />;
  const { inventory } = read;
  if (inventoryIsEmpty(inventory)) return <Empty message={`${subject} ${NOT_REPORTED}`} />;
  const groups = groupInventory(inventory);
  return (
    <>
      <p class="card-note">{inventory.discoveredAt ? `Discovered ${new Date(inventory.discoveredAt).toLocaleString()}.` : "Never discovered."}</p>
      <div class="table-scroll">
        <table class="table">
          <thead>
            <tr>
              <th scope="col">Model</th>
              <th scope="col">Provider</th>
              <th scope="col">Can be called</th>
              <th scope="col">Capabilities</th>
            </tr>
          </thead>
          <tbody>
            {groups.flatMap((group) =>
              group.rows.map((row) => (
                <tr key={`${group.provider}-${row.bedrockId}`}>
                  <td>
                    {row.displayName}
                    {!row.priced && (
                      <>
                        {" "}
                        <Chip tone="amber" title="No price is set for this model yet, so its usage shows no cost.">
                          unpriced
                        </Chip>
                      </>
                    )}
                    <span class="cell-sub mono">{row.publicName}</span>
                  </td>
                  <td>{group.provider}</td>
                  <td>
                    <Chip tone={row.invokable ? "green" : "red"}>{row.invokabilityLabel}</Chip>
                    {row.reason && <span class="field-hint"> {row.reason}</span>}
                  </td>
                  <td>
                    {row.capabilities.map((chip) => (
                      <Chip key={chip.name} tone={chip.tone} title={chip.state === "unknown" ? "Not verified" : chip.state === "true" ? "Supported" : "Not supported"}>
                        {chip.label}
                        {chip.state === "unknown" ? "?" : chip.state === "false" ? " ✕" : ""}
                      </Chip>
                    ))}
                    {row.unverified && <Chip tone="amber">unverified</Chip>}
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ the three settings sub-tabs

/** What each settings sub-tab reads: one scope's documents and catalog, and how to write it. */
interface SettingsTabProps {
  surface: Surface;
  scope: string;
  /** The installation's name, which a person reads in place of its id; null at the org default. */
  scopeName: string | null;
  loaded: Loaded;
  /** The models the scope's inventories name: the installation's, or every active one's at the org default. */
  catalog: CatalogModel[];
  /** Writes a document for the scope with the version read. */
  write(document: JsonObject): Promise<void>;
  onOpenOrg: (() => void) | undefined;
  onSaved(): void;
  /** Told whether the sub-tab's draft differs from what was loaded (the scope selector's guard). */
  onDirty(dirty: boolean): void;
}

/** The saved document as the scope sees it: the org default under the installation's override. */
function savedOf(loaded: Loaded): JsonObject {
  return merge(loaded.inherited ?? {}, loaded.document) as JsonObject;
}

/** A sub-tab's one write at a time: `run` writes, then `onDone` and the screen's reload; a refusal stays in `failure`. */
function useWrite(write: SettingsTabProps["write"], onSaved: () => void) {
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const run = (document: JsonObject, onDone?: () => void) => {
    setSaving(true);
    setFailure(null);
    write(document)
      .then(() => {
        onDone?.();
        onSaved();
      })
      .catch((err: unknown) => setFailure(unexpected(err)))
      .finally(() => setSaving(false));
  };
  return { saving, failure, setFailure, run };
}

const READ_ONLY_NOTE = "The settings are read-only right now.";

interface ModalState {
  provider: string;
  /** The row's switch asked for this first. */
  enabled?: boolean;
}

/**
 * Families (SCR-589): the global allow-list by provider. A row's switch saves at once; the modal is
 * the sub-tab's draft, guarded while it differs from the saved entry, and saves on its own Save.
 * Writes `models.allowed_models`, and the default of an agent the person re-pointed in the modal.
 */
function FamiliesTab({ scope, loaded, catalog, write, onOpenOrg, onSaved }: SettingsTabProps) {
  const { readOnly, reason } = useReadOnly();
  const [modal, setModal] = useState<ModalState | null>(null);
  const { saving, failure, setFailure, run } = useWrite(write, onSaved);
  const saved = savedOf(loaded);

  const saveProvider = (result: ProviderEditResult) => run(result.document, () => setModal(null));
  const toggleProvider = (provider: string, enabled: boolean) => {
    const entry = entryFor(globalOf(saved), provider);
    const result = applyProviderEdit({}, saved, provider, { enabled, mode: entry.mode, allowed: entry.allowed }, catalog);
    if (result.ready) saveProvider(result);
    else setModal({ provider, enabled });
  };

  return (
    <>
      <ProvidersSection rows={providerRows(saved, catalog)} editable={scope === ""} disabledNote={readOnly ? (reason ?? READ_ONLY_NOTE) : saving ? "Saving…" : null} onEdit={(provider) => setModal({ provider })} onToggle={toggleProvider} onOpenOrg={onOpenOrg} />
      {failure && !modal && <ErrorBanner error={failure} details={true} />}
      {modal && (
        <ProviderModal
          provider={modal.provider}
          effective={saved}
          catalog={catalog}
          initialEnabled={modal.enabled}
          saving={saving}
          failure={failure}
          onSave={saveProvider}
          onClose={() => {
            setModal(null);
            setFailure(null);
          }}
        />
      )}
    </>
  );
}

/** Fallbacks (SCR-590): the org's chains alone, with their own Save. Writes `models.fallbacks` over the saved document. */
function FallbacksTab({ scope, loaded, catalog, write, onOpenOrg, onSaved, onDirty }: SettingsTabProps) {
  const { readOnly, reason } = useReadOnly();
  const [draft, setDraft] = useState<JsonObject>(loaded.document);
  const { saving, failure, setFailure, run } = useWrite(write, onSaved);
  const changed = JSON.stringify(draft) !== JSON.stringify(loaded.document);
  useDraftGuard(changed, onDirty);
  const editable = scope === "";
  const rowErrors = failure ? fallbackErrorsFrom(failure) : {};

  return (
    <FallbacksSection
      draft={editable ? draft : savedOf(loaded)}
      catalog={catalog}
      editable={editable}
      disabledNote={readOnly ? (reason ?? READ_ONLY_NOTE) : null}
      errors={rowErrors}
      failure={failure && Object.keys(rowErrors).length === 0 ? failure : null}
      changed={changed}
      saving={saving}
      onChange={(next) => {
        setFailure(null);
        setDraft(next);
      }}
      onSave={() => run(setPath(loaded.document, FALLBACKS_PATH, getPath(draft, FALLBACKS_PATH) ?? []))}
      onOpenOrg={onOpenOrg}
    />
  );
}

/** Agents (SCR-590): the harness note, the applied line and one card per agent, with one Save. Writes the agents' `models.<harness>` fields. */
function AgentsTab({ surface, scopeName, loaded, catalog, write, onSaved, onDirty }: SettingsTabProps) {
  const { readOnly, reason } = useReadOnly();
  const [draft, setDraft] = useState<JsonObject>(loaded.document);
  const { saving, failure, setFailure, run } = useWrite(write, onSaved);
  const changed = JSON.stringify(draft) !== JSON.stringify(loaded.document);
  useDraftGuard(changed, onDirty);

  const effective = (merge(loaded.inherited ?? {}, draft) as JsonObject | undefined) ?? {};
  const schemaErrors = validate(effective);
  const unplaced = failure && !isPlacedRefusal(failure) ? failure : null;
  const lists = effectiveLists(loaded.effectiveModels, savedOf(loaded), effective, catalog);
  const narrow = Object.fromEntries(HARNESSES.map((h) => [h.key, narrowChoices(effective, h, catalog)])) as Record<Harness["key"], ReturnType<typeof narrowChoices>>;

  return (
    <>
      <Card title="A harness setting is not a security control" help={NOTE_HELP}>
        <p class="card-note">{HARNESS_NOTE}</p>
      </Card>
      {loaded.layers && <AppliedLine layers={loaded.layers} />}
      <AgentsSection
        label={scopeName === null ? "Model settings" : `Model settings for ${scopeName}`}
        cards={agentCards(effective, lists, catalog)}
        narrow={narrow}
        effective={effective}
        inherited={(path) => loaded.inherited !== undefined && isInherited(draft, path)}
        pins={loaded.pins}
        surface={surface}
        errors={failure ? agentErrorsFrom(failure) : {}}
        fieldErrors={{ ...schemaErrors, ...(unplaced ? fieldErrorsFrom(unplaced) : {}) }}
        failure={unplaced}
        saving={saving}
        canSave={changed && !saving && !readOnly && !hasErrors(schemaErrors)}
        readOnlyReason={readOnly ? (reason ?? "") : null}
        onChange={(path: string, value: Json | undefined) => {
          setFailure(null);
          setDraft((current) => setPath(current, path, value));
        }}
        onSave={() => run(draft)}
      />
    </>
  );
}

function AppliedLine({ layers }: { layers: SettingsLayers }) {
  const summary = appliedSummary(layers);
  return (
    <p class="card-note" role="status">
      {summary.text}
      {summary.lkgOnly > 0 && (
        <>
          {" "}
          <Chip tone="amber" title="The API was unreachable, so these tasks serve the sealed last known good document.">
            {summary.lkgOnly} on last known good
          </Chip>
        </>
      )}
    </p>
  );
}
